import { AppModule } from "@/store/modules/app";
import {
  IJBRouteResult,
  IMultiStartRoute,
  IResult,
  IRouteHitTestParam,
  IRouteHitTestResultParam,
} from "@/types/map";
import along from "@turf/along";
import distance from "@turf/distance";
import * as turf from "@turf/helpers";
import length from "@turf/length";
import lineSlice from "@turf/line-slice";
import Axios from "axios";
import { loadModules } from "esri-loader";
import gcoord from "gcoord";
import $ from "jquery";

enum Platform {
  NavInfo = "NavInfo",
  AMap = "AMap",
  BMap = "BMap",
  MapABC = "MapABC",
}

export default class DirectionModel_ArcGIS {
  private static intances: Map<string, DirectionModel_ArcGIS>;
  public static getInstance(view: __esri.View) {
    const id = view.container.id;
    if (!DirectionModel_ArcGIS.intances) {
      DirectionModel_ArcGIS.intances = new Map();
    }
    let instance = DirectionModel_ArcGIS.intances.get(id);
    if (!instance) {
      instance = new DirectionModel_ArcGIS(view);
      DirectionModel_ArcGIS.intances.set(id, instance);
    }
    return instance;
  }

  private view: __esri.View;
  private routeServiceUrl!: string;
  private reverseGeoCodeServiceUrl!: string;

  private platform = Platform.NavInfo;

  private startPoint: __esri.Point | undefined;
  private endPoint: __esri.Point | undefined;
  private wayPoints: Array<__esri.Point> = [];

  private startGraphic!: __esri.Graphic;
  private endGraphic!: __esri.Graphic;
  private wayGraphics: Array<__esri.Graphic> = [];
  private routeCrossGraphics: Array<__esri.Graphic> = [];

  private routeResults: Map<string, IJBRouteResult> = new Map();
  private strategyGraphics: Map<string, Array<__esri.Graphic>> = new Map();

  /** 道路图层 */
  private roadLayer!: __esri.FeatureLayer;
  /** 路径图层 */
  private routeLayer!: __esri.FeatureLayer;
  /** 路口图层 */
  private crossLayer!: __esri.FeatureLayer;
  /** 信号机图层 */
  private signalLayer!: __esri.FeatureLayer;
  /** 摄像机图层 */
  private cameraLayer!: __esri.FeatureLayer;
  /** 路径回放图层 */
  private graphicsLayer!: __esri.GraphicsLayer;

  /** 信号机点位，用于回放时触发intoSignal/outofSignal事件 */
  private signalGraphics: __esri.Graphic[] = [];

  private Graphic!: __esri.GraphicConstructor;
  private geometryEngineAsync!: __esri.geometryEngineAsync;
  private Polyline!: __esri.PolylineConstructor;
  private Polygon!: __esri.PolygonConstructor;
  private FeatureLayer!: __esri.FeatureLayerConstructor;
  private webMercatorUtils!: __esri.webMercatorUtils;

  private readonly signalLayerUrl = `http://${AppModule.appConfig.gisServer}/arcgis/rest/services/ZhongZhi/RoadNetwork/MapServer/0`;
  private readonly crossLayerUrl = `http://${AppModule.appConfig.gisServer}/arcgis/rest/services/ZhongZhi/RoadNetwork/MapServer/1`;
  private readonly roadLayerUrl = `http://${AppModule.appConfig.gisServer}/arcgis/rest/services/ZhongZhi/RoadNetwork/MapServer/2`;

  constructor(view: __esri.View) {
    this.view = view;
    if (AppModule.appConfig.routeServiceUrl) {
      this.routeServiceUrl = AppModule.appConfig.routeServiceUrl;
      this.routeServiceUrl = this.routeServiceUrl.replace(
        /{gisserver}/i,
        AppModule.appConfig.gisServer
      );
    }
    if (AppModule.appConfig.reverseGeoCodeServiceUrl) {
      this.reverseGeoCodeServiceUrl =
        AppModule.appConfig.reverseGeoCodeServiceUrl;
      this.reverseGeoCodeServiceUrl = this.reverseGeoCodeServiceUrl.replace(
        /{gisserver}/i,
        AppModule.appConfig.gisServer
      );
    }

    this.platform =
      (AppModule.appConfig.routePlatform as Platform) || Platform.NavInfo;
  }

  public async initialize(params?: {
    signalUrl?: string;
    cameraUrl?: string;
    crossUrl?: string;
    token?: string;
    showRoad?: boolean;
  }) {
    type MapModules = [
      typeof import("esri/geometry/Polyline"),
      typeof import("esri/geometry/Polygon"),
      typeof import("esri/Graphic"),
      typeof import("esri/layers/FeatureLayer"),
      typeof import("esri/layers/GraphicsLayer"),
      typeof import("esri/geometry/geometryEngineAsync"),
      typeof import("esri/geometry/support/webMercatorUtils")
    ];
    const [
      Polyline,
      Polygon,
      Graphic,
      FeatureLayer,
      GraphicsLayer,
      geometryEngineAsync,
      webMercatorUtils,
    ] = await (loadModules([
      "esri/geometry/Polyline",
      "esri/geometry/Polygon",
      "esri/Graphic",
      "esri/layers/FeatureLayer",
      "esri/layers/GraphicsLayer",
      "esri/geometry/geometryEngineAsync",
      "esri/geometry/support/webMercatorUtils",
    ]) as Promise<MapModules>);
    this.Polyline = Polyline;
    this.Polygon = Polygon;
    this.Graphic = Graphic;
    this.geometryEngineAsync = geometryEngineAsync;
    this.FeatureLayer = FeatureLayer;
    this.webMercatorUtils = webMercatorUtils;

    if (this.platform === Platform.NavInfo) {
      this.crossLayer = new FeatureLayer({
        url: this.crossLayerUrl,
        outFields: ["*"],
      });

      this.roadLayer = new FeatureLayer({
        url: this.roadLayerUrl,
        renderer: {
          type: "simple",
          symbol: {
            type: "simple-line",
            color: "#607d8b",
          },
        } as any,
        visible: !(params?.showRoad === false),
      });
      this.view.map.add(this.roadLayer);

      this.signalLayer = new FeatureLayer({
        url: this.signalLayerUrl,
        outFields: ["*"],
      });
    }

    if (params && params.signalUrl) {
      await this.getAllSignals(params.signalUrl, params.token);
    }

    if (params && params.crossUrl) {
      await this.getAllCrosses(params.crossUrl, params.token);
    }

    if (params && params.cameraUrl) {
      await this.getAllCameras(params.cameraUrl, params.token);
    }

    this.routeLayer = new FeatureLayer({
      source: [],
      objectIdField: "OBJECTID",
      geometryType: "polyline",
      fields: [
        {
          name: "OBJECTID",
          type: "oid",
        },
        {
          name: "status",
          type: "string",
        },
        {
          name: "strategy",
          type: "string",
        },
      ],
      renderer: {
        type: "unique-value",
        field: "status",
        // 待选路径
        defaultSymbol: {
          type: "simple-line",
          color: "#bdbdbd",
          width: 6,
        },
        uniqueValueInfos: [
          // 高亮路径带箭头
          {
            value: "highlight",
            symbol: {
              type: "cim",
              data: {
                type: "CIMSymbolReference",
                symbol: {
                  type: "CIMLineSymbol",
                  symbolLayers: [
                    {
                      type: "CIMSolidStroke",
                      enable: true,
                      width: 8,
                      color: [0, 189, 44, 255],
                    },
                    {
                      type: "CIMVectorMarker",
                      enable: true,
                      size: 6,
                      markerPlacement: {
                        type: "CIMMarkerPlacementAlongLineSameSize",
                        endings: "WithMarkers",
                        placementTemplate: [20],
                      },
                      frame: {
                        xmin: -6,
                        ymin: -6,
                        xmax: 6,
                        ymax: 6,
                      },
                      markerGraphics: [
                        {
                          type: "CIMMarkerGraphic",
                          geometry: {
                            paths: [
                              [
                                [-8, 5],
                                [0, 0],
                                [-8, -5],
                              ],
                            ],
                          },
                          symbol: {
                            type: "CIMLineSymbol",
                            symbolLayers: [
                              {
                                type: "CIMSolidStroke",
                                enable: true,
                                color: [255, 255, 255, 255],
                                width: 2,
                              },
                            ],
                          },
                        },
                      ],
                    },
                  ],
                },
              },
            },
          },
        ],
      } as any,
    });
    this.view.map.add(this.routeLayer);

    this.graphicsLayer = new GraphicsLayer();
    this.view.map.add(this.graphicsLayer);
  }

  public async getPoint(
    type: string,
    index: number = 0
  ): Promise<{ name: string; point: Array<number> }> {
    return new Promise((resolve) => {
      $(`#${this.view.container.id}`).css("cursor", "crosshair");

      const handle = this.view.on("click", async (event) => {
        let mapPoint = event.mapPoint;
        if (mapPoint.spatialReference.isWebMercator) {
          mapPoint = this.webMercatorUtils.webMercatorToGeographic(
            mapPoint
          ) as __esri.Point;
        }
        handle.remove();
        $(`#${this.view.container.id}`).css("cursor", "default");
        switch (type) {
          case "start": {
            this.startPoint = mapPoint;
            if (!this.startGraphic) {
              this.startGraphic = new this.Graphic({
                geometry: mapPoint,
                symbol: {
                  type: "picture-marker",
                  url: "assets/mapIcons/begin.png",
                  width: "48px",
                  height: "55px",
                  yoffset: "20px",
                } as any,
              });
              this.graphicsLayer.add(this.startGraphic);
            } else {
              this.startGraphic.geometry = mapPoint;
            }

            break;
          }

          case "end": {
            this.endPoint = mapPoint;
            if (!this.endGraphic) {
              this.endGraphic = new this.Graphic({
                geometry: mapPoint,
                symbol: {
                  type: "picture-marker",
                  url: "assets/mapIcons/end.png",
                  width: "48px",
                  height: "55px",
                  yoffset: "20px",
                } as any,
              });
              this.graphicsLayer.add(this.endGraphic);
            } else {
              this.endGraphic.geometry = mapPoint;
            }
            break;
          }

          case "way": {
            const wayGraphic = new this.Graphic({
              geometry: mapPoint,
              symbol: {
                type: "picture-marker",
                url: "assets/mapIcons/discordant-cross.png",
                width: "42px",
                height: "42px",
              } as any,
            });
            this.graphicsLayer.add(wayGraphic);

            if (index >= this.wayPoints.length) {
              this.wayPoints.push(mapPoint);
            } else {
              this.wayPoints.splice(index, 0, mapPoint);
            }
            this.wayGraphics.push(wayGraphic);

            if (this.wayPoints.length > 5) {
              this.wayPoints.shift();

              this.graphicsLayer.remove(this.wayGraphics[0]);
              this.wayGraphics.shift();
            }
            break;
          }
        }
        const name = await this.getPointName(mapPoint);
        resolve({
          name,
          point: [mapPoint.longitude, mapPoint.latitude],
        });
      });
    });
  }

  public deleteWayPoint(index: number): IResult {
    if (index < this.wayPoints.length) {
      this.wayPoints.splice(index, 1);
      const removeGraphic = this.wayGraphics.splice(index, 1)[0];
      this.graphicsLayer.remove(removeGraphic);
      return { status: 0, message: "" };
    } else {
      return { status: -1, message: "wrong index value" };
    }
  }

  public async makeRoute(): Promise<IResult> {
    if (!this.startPoint) {
      return { status: -1, message: "没有起点" };
    }

    if (!this.endPoint) {
      return { status: -1, message: "没有终点" };
    }

    // 清除现有路径
    const currentGraphics = (
      await this.routeLayer.queryFeatures(this.routeLayer.createQuery())
    ).features;
    await this.routeLayer.applyEdits({ deleteFeatures: currentGraphics });
    // 清除路口点
    this.graphicsLayer.removeMany(this.routeCrossGraphics);
    this.routeCrossGraphics = [];
    // 清除结果记录
    this.routeResults.clear();
    this.strategyGraphics.clear();

    const startPointValue = this.getPointValue(this.startPoint);
    const endPointValue = this.getPointValue(this.endPoint);
    let wayPointsValue = "";
    this.wayPoints.forEach((point) => {
      wayPointsValue += this.getPointValue(point) + ";";
    });
    wayPointsValue = wayPointsValue.substr(0, wayPointsValue.length - 1);

    const routes: Array<IJBRouteResult> = [];
    const strategies = ["0", "1", "2", "4"];
    for (let strategy of strategies) {
      const routeResult = await this.makeRouteByStrategy(
        startPointValue,
        endPointValue,
        wayPointsValue,
        strategy
      );
      if (routeResult) {
        routeResult.strategy = strategy;
        routes.push(routeResult);
        this.routeResults.set(strategy, routeResult);
      }
    }

    return { status: 0, message: "", result: routes };
  }

  private async makeRouteByStrategy(
    startPointValue: string,
    endPointValue: string,
    wayPointsValue: string,
    strategy: string = "1",
    lineStatus: string = "normal"
  ) {
    const requestParams =
      wayPointsValue && wayPointsValue !== ""
        ? {
            origin: startPointValue,
            destination: endPointValue,
            waypoints: wayPointsValue,
          }
        : {
            origin: startPointValue,
            destination: endPointValue,
          };
    const url = this.routeServiceUrl
      .replace(/{startPoint}/i, startPointValue)
      .replace(/{endPoint}/i, endPointValue)
      .replace(/{wayPoints}/i, wayPointsValue)
      .replace(/{strategy}/i, strategy);
    // const url = "/data/PuDong_route.json";
    const responseData = (await Axios.get(url)).data;
    let routeInfo;
    switch (this.platform) {
      // 四维图新
      case Platform.NavInfo:
        routeInfo = await this.dealResponse_NavInfo(responseData, strategy);
        return routeInfo;

      // 高德内网
      case Platform.MapABC:
        routeInfo = await this.dealResponse_MapABC(
          responseData,
          strategy,
          lineStatus
        );
        return routeInfo;
    }
  }

  /** 处理四维图新的返回数据 */
  private async dealResponse_NavInfo(
    responseData: any,
    strategy: string
  ): Promise<IJBRouteResult | undefined> {
    if (responseData.data.rows.length > 0) {
      const row = responseData.data.rows[0];
      const routeString: string = row.routelatlon;
      const pointsArray = routeString.split(";");
      const path: Array<Array<number>> = [];
      pointsArray.forEach((pointString) => {
        const coord = pointString.split(",");
        if (coord.length === 2) {
          // 四维使用火星坐标系
          const transCoord = gcoord.transform(
            coord,
            gcoord.GCJ02,
            gcoord.WGS84
          ) as number[];
          path.push(transCoord);
        }
      });
      const routeGraphic = new this.Graphic({
        geometry: {
          type: "polyline",
          paths: [path],
        } as any,
        attributes: {
          status: "normal",
          strategy,
        },
      });
      await this.routeLayer.applyEdits({ addFeatures: [routeGraphic] });

      const crossInfos = await this.findCrossInRoute(strategy, path);

      return {
        distance: row.distance,
        duration: row.duration,
        crosses: crossInfos,
        coordinates: routeString,
      };
    }
  }

  /** 处理高德内网的返回数据 */
  private async dealResponse_MapABC(
    responseData: any,
    strategy: string,
    lineStatus: string
  ): Promise<IJBRouteResult | undefined> {
    if (responseData.message === "ok") {
      const { steps, duration, distance } = responseData.result.routes[0];
      let routeString = "";
      steps.forEach((step: any) => {
        routeString += step.path + ";";
      });
      routeString = routeString.substr(0, routeString.length - 1);
      const pointsArray = routeString.split(";");

      const path: Array<Array<number>> = [];
      pointsArray.forEach((pointString) => {
        const coord = pointString.split(",");
        path.push([Number(coord[0]), Number(coord[1])]);
      });
      const routeGraphic = new this.Graphic({
        geometry: {
          type: "polyline",
          paths: [path],
        } as any,
        attributes: {
          status: lineStatus,
          strategy,
        },
      });
      await this.routeLayer.applyEdits({ addFeatures: [routeGraphic] });

      const crossInfos = await this.findCrossInRoute(strategy, path);

      return {
        distance,
        duration,
        crosses: crossInfos,
        coordinates: routeString,
      };
    }
  }

  /**
   * 搜索路径经过的路口，以及路口周边的摄像机、信号机
   * 先做缓冲搜索，然后获取缓冲区内的路口在路径上的投影点
   * 用起点到投影点的距离给路口排序
   * @param strategy 路径策略
   * @param path 路径点集
   * @returns
   */
  private async findCrossInRoute(strategy: string, path: Array<Array<number>>) {
    const turfLine = turf.lineString(path);

    const crossInfos: Array<{
      id: string;
      name: string;
      signal?: { id: string; name: string };
      camera?: { id: string; name: string };
      lengthFromStart: number;
    }> = [];

    const line = new this.Polyline({ paths: [path] });
    const routeBuffer = (await this.geometryEngineAsync.geodesicBuffer(
      line,
      50,
      "meters"
    )) as __esri.Polygon;

    const crossQuery = this.crossLayer.createQuery();
    crossQuery.geometry = routeBuffer;
    const crossGraphics = (await this.crossLayer.queryFeatures(crossQuery))
      .features;

    let nodeIds = "";
    for (let crossGraphic of crossGraphics) {
      const nodeId =
        crossGraphic.attributes["NodeID"] || crossGraphic.attributes["id"];
      if (nodeIds.includes(nodeId)) {
        continue;
      }
      nodeIds += nodeId;

      crossGraphic.symbol = {
        type: "picture-marker",
        url: "assets/mapIcons/road_key.png",
        width: "32px",
        height: "45px",
        yoffset: "15px",
      } as any;

      const crossName = crossGraphic.attributes["Name_1"]
        ? `${crossGraphic.attributes["Name_1"]}-${crossGraphic.attributes["Name_2"]}`
        : crossGraphic.attributes["name"];

      // 路口点在路径上的投影
      const nearestPoint = (
        await this.geometryEngineAsync.nearestCoordinate(
          line,
          crossGraphic.geometry as __esri.Point
        )
      ).coordinate;

      // 起点到投影点的线段
      const slicedLine = lineSlice(
        turf.point(path[0]),
        turf.point([nearestPoint.x, nearestPoint.y]),
        turfLine
      );
      // 起点到投影点的距离
      const lengthFromStart = length(slicedLine, { units: "meters" });

      // 距离路口最近的信号机和摄像机
      const signalAndCamera = await this.getPointSignalAndCamera(
        crossGraphic.geometry as __esri.Point
      );

      // 没有信号机的路口不显示
      // if (signalAndCamera.signalInfo.id !== "") {
      crossInfos.push({
        id: crossGraphic.attributes["ID"] || crossGraphic.attributes["id"],
        name: crossName,
        camera: signalAndCamera.cameraInfo,
        signal: signalAndCamera.signalInfo,
        lengthFromStart,
      });

      // 按策略保存搜索结果
      if (strategy !== "") {
        if (this.strategyGraphics.has(strategy)) {
          this.strategyGraphics.get(strategy)?.push(crossGraphic);
        } else {
          this.strategyGraphics.set(strategy, [crossGraphic]);
        }
      }
      // }
    }

    // 按路口距离排序
    const sortedCrosses = crossInfos.sort(
      (cross1, cross2) => cross1.lengthFromStart - cross2.lengthFromStart
    );

    return sortedCrosses;
  }

  /** 数组转为逗号分隔 */
  private getPointValue(point: __esri.Point): string {
    if (this.platform === Platform.NavInfo) {
      const transCoord = gcoord.transform(
        [point.longitude, point.latitude],
        gcoord.WGS84,
        gcoord.GCJ02
      ) as number[];
      return transCoord[0].toFixed(6) + "," + transCoord[1].toFixed(6);
    } else {
      return point.longitude.toFixed(6) + "," + point.latitude.toFixed(6);
    }
  }

  /**
   * 点位名称
   */
  private async getPointName(point: __esri.Point): Promise<string> {
    let pointName = "";
    switch (this.platform) {
      // 四维没有提供地理编码服务，搜索道路名称
      case Platform.NavInfo:
        pointName = await this.getNearbyRoadName(point);
        break;

      case Platform.MapABC:
        pointName = await this.reverseGeocodingInMapABC(point);
        break;

      default:
        break;
    }
    return pointName;
  }

  /** 获取附近的路口/道路名称
   * 优先查找路口，没找到路口再找道路 */
  private async getNearbyRoadName(point: __esri.Point): Promise<string> {
    const pointBuffer = (await this.geometryEngineAsync.geodesicBuffer(
      point,
      50,
      "meters"
    )) as __esri.Polygon;
    let query = this.crossLayer.createQuery();
    query.geometry = pointBuffer;
    const crossGraphics = (await this.crossLayer.queryFeatures(query)).features;
    if (crossGraphics.length > 0) {
      const cross = crossGraphics[0];
      return cross.attributes["Name_1"] + "-" + cross.attributes["Name_2"];
    } else {
      query = this.roadLayer.createQuery();
      query.geometry = pointBuffer;
      const roadGraphics = (await this.roadLayer.queryFeatures(query)).features;
      if (roadGraphics.length > 0) {
        const road = roadGraphics[0];
        return road.attributes["PathName"];
      }
    }
    return "";
  }

  /** 调用MapABC的逆地理编码获取点位名称 */
  private async reverseGeocodingInMapABC(point: __esri.Point): Promise<string> {
    if (point.spatialReference.isWebMercator) {
      point = this.webMercatorUtils.webMercatorToGeographic(
        point
      ) as __esri.Point;
    }
    const { x, y } = point;
    // 逆地理编码的服务地址
    const rgcUrl = this.reverseGeoCodeServiceUrl.replace(
      /{location}/i,
      x + "," + y
    );
    const responseData = (await Axios.get(rgcUrl)).data;
    return responseData.result[0].formatted_address;
  }

  /**
   * 获取所有摄像机位置，并保存到FeatureLayer
   * @param cameraServiceUrl 摄像机服务地址
   */
  private async getAllCameras(cameraServiceUrl: string, token?: string) {
    if (this.platform === Platform.NavInfo) {
      const responseData = await Axios.post(
        cameraServiceUrl,
        {
          pageIndex: 1,
          pageSize: 99999999,
        },
        { headers: { token } }
      );
      if (responseData.data && responseData.data.success) {
        const cameraData: Array<{
          fstrCameraId: string;
          longitude: number;
          latitude: number;
          camName: string;
        }> = responseData.data.data.data;
        const cameraGraphics = cameraData.map((cameraInfo) => {
          return new this.Graphic({
            geometry: {
              type: "point",
              x: cameraInfo.longitude,
              y: cameraInfo.latitude,
            } as any,
            attributes: {
              id: cameraInfo.fstrCameraId,
              name: cameraInfo.camName,
            },
          });
        });
        this.cameraLayer = new this.FeatureLayer({
          source: cameraGraphics,
          objectIdField: "OBJECTID",
          geometryType: "point",
          fields: [
            {
              name: "OBJECTID",
              type: "oid",
            },
            {
              name: "id",
              type: "string",
            },
            {
              name: "name",
              type: "string",
            },
          ],
        });
      }
    } else if (this.platform === Platform.MapABC) {
      const responseData = cameraServiceUrl.endsWith("json")
        ? await Axios.get(cameraServiceUrl)
        : await Axios.post(
            cameraServiceUrl,
            {
              pageIndex: 1,
              pageSize: 99999999,
            },
            { headers: { token } }
          );
      const cameraData: {
        devId: string;
        devName: string;
        longitude: number;
        latitude: number;
      }[] = responseData.data.data.data;

      const cameraGraphics: __esri.Graphic[] = [];
      cameraData.forEach((data) => {
        const x = data.longitude,
          y = data.latitude;
        if (x && y) {
          const graphic = new this.Graphic({
            geometry: {
              type: "point",
              x,
              y,
            } as any,
            attributes: {
              id: data.devId,
              name: data.devName,
            },
          });
          cameraGraphics.push(graphic);
        }
      });

      this.cameraLayer = new this.FeatureLayer({
        source: cameraGraphics,
        objectIdField: "OBJECTID",
        geometryType: "point",
        visible: false,
        fields: [
          {
            name: "OBJECTID",
            type: "oid",
          },
          {
            name: "id",
            type: "string",
          },
          {
            name: "name",
            type: "string",
          },
        ],
      });
      this.view.map.add(this.cameraLayer);
    }
  }

  /** 获取所有信号机位置，并保存到FeatureLayer */
  private async getAllSignals(signalServiceUrl: string, token?: string) {
    const responseData = signalServiceUrl.endsWith("json")
      ? await Axios.get(signalServiceUrl)
      : await Axios.post(
          signalServiceUrl,
          {
            pageIndex: 1,
            pageSize: 99999999,
          },
          { headers: { token } }
        );
    if (responseData.data && responseData.data.success) {
      const signalData: {
        signalNo: string;
        crossName: string;
        longitude: number;
        latitude: number;
      }[] = responseData.data.data.data;

      const signalGraphics: __esri.Graphic[] = [];
      signalData.forEach((data) => {
        const x = data.longitude,
          y = data.latitude;
        if (x && y) {
          const graphic = new this.Graphic({
            geometry: {
              type: "point",
              x,
              y,
            } as any,
            attributes: {
              id: data.signalNo,
              name: data.crossName,
            },
          });
          signalGraphics.push(graphic);
        }
      });

      this.signalLayer = new this.FeatureLayer({
        source: signalGraphics,
        objectIdField: "OBJECTID",
        geometryType: "point",
        visible: false,
        fields: [
          {
            name: "OBJECTID",
            type: "oid",
          },
          {
            name: "id",
            type: "string",
          },
          {
            name: "name",
            type: "string",
          },
        ],
      });
      this.view.map.add(this.signalLayer);
    }
  }

  /** 获取所有路口位置，并保存到FeatureLayer */
  private async getAllCrosses(crossServiceUrl: string, token?: string) {
    const responseData = crossServiceUrl.endsWith("json")
      ? await Axios.get(crossServiceUrl)
      : await Axios.post(
          crossServiceUrl,
          {
            pageIndex: 1,
            pageSize: 99999999,
          },
          { headers: { token } }
        );
    if (responseData.data && responseData.data.success) {
      const crossData: {
        crossNo: string;
        crossName: string;
        longitude: number;
        latitude: number;
      }[] = responseData.data.data.data;

      const existCrossId: string[] = [];
      const crossGraphics: __esri.Graphic[] = [];
      crossData.forEach((data) => {
        const crossId = data.crossNo,
          x = data.longitude,
          y = data.latitude;
        // 过滤重复数据
        if (!existCrossId.includes(crossId) && !isNaN(x) && !isNaN(y)) {
          existCrossId.push(crossId);
          const graphic = new this.Graphic({
            geometry: {
              type: "point",
              x,
              y,
            } as any,
            attributes: {
              id: crossId,
              name: data.crossName,
            },
          });
          crossGraphics.push(graphic);
        }
      });

      this.crossLayer = new this.FeatureLayer({
        source: crossGraphics,
        objectIdField: "OBJECTID",
        geometryType: "point",
        visible: false,
        fields: [
          {
            name: "OBJECTID",
            type: "oid",
          },
          {
            name: "id",
            type: "string",
          },
          {
            name: "name",
            type: "string",
          },
        ],
      });
      this.view.map.add(this.crossLayer);
    }
  }

  /** 查找点位附近信号机和摄像机 */
  private async getPointSignalAndCamera(point: __esri.Point) {
    let signalInfo = { id: "", name: "" },
      cameraInfo = { id: "", name: "" };
    const pointBuffer = (await this.geometryEngineAsync.geodesicBuffer(
      point,
      50,
      "meters"
    )) as __esri.Polygon;

    // 信号机
    if (this.signalLayer) {
      const query = this.signalLayer.createQuery();
      query.geometry = pointBuffer;
      query.outFields = ["*"];
      const signalGraphics = (await this.signalLayer.queryFeatures(query))
        .features;
      if (signalGraphics.length > 0) {
        const aroundPoints = signalGraphics.map(
          (graphic) => graphic.geometry as __esri.Point
        );
        const minDistanceIndex = this.nearestPoint(point, aroundPoints);
        signalInfo = {
          id:
            signalGraphics[minDistanceIndex].attributes["FSTR_SCATS"] ||
            signalGraphics[minDistanceIndex].attributes["id"],
          name:
            signalGraphics[minDistanceIndex].attributes["FSTR_DESC"] ||
            signalGraphics[minDistanceIndex].attributes["name"],
        };
      }
    }

    // 摄像机
    if (this.cameraLayer) {
      const query = this.cameraLayer.createQuery();
      query.geometry = pointBuffer;
      query.outFields = ["*"];
      const cameraGraphics = (await this.cameraLayer.queryFeatures(query))
        .features;
      if (cameraGraphics.length > 0) {
        const aroundPoints = cameraGraphics.map(
          (graphic) => graphic.geometry as __esri.Point
        );
        const minDistanceIndex = this.nearestPoint(point, aroundPoints);
        cameraInfo = {
          id: cameraGraphics[minDistanceIndex].attributes["id"],
          name: cameraGraphics[minDistanceIndex].attributes["name"],
        };
      }
    }

    return { signalInfo, cameraInfo };
  }

  /** 哪个点和给定点最近 */
  private nearestPoint(
    centerPoint: __esri.Point,
    aroundPoints: Array<__esri.Point>
  ) {
    const centerPointTurf = turf.point([centerPoint.x, centerPoint.y]);
    let minDistanceIndex = 0;
    let minDistance = Number.MAX_VALUE;
    aroundPoints.forEach((point, idnex) => {
      const aroundPointTurf = turf.point([point.x, point.y]);
      const pointDistance = distance(centerPointTurf, aroundPointTurf, {
        units: "meters",
      });
      if (pointDistance < minDistance) {
        minDistance = pointDistance;
        minDistanceIndex = idnex;
      }
    });

    return minDistanceIndex;
  }

  /** "x1,y1;x2,y2" -> [[x1, y1], [x2, y2]] */
  private pathString2PathArray(coordinates: string) {
    const pointsArray = coordinates.split(";");
    const path: Array<Array<number>> = [];
    pointsArray.forEach((pointString) => {
      const coord = pointString.split(",");
      if (coord.length === 2) {
        const transCoord = gcoord.transform(
          coord,
          gcoord.GCJ02,
          gcoord.WGS84
        ) as number[];
        path.push(transCoord);
      }
    });
    return path;
  }

  private async showRouteInCoordinates(
    coordinates: string,
    showPoint: boolean = true
  ) {
    const path = this.pathString2PathArray(coordinates);

    // 起点
    if (showPoint) {
      this.startGraphic = new this.Graphic({
        geometry: { type: "point", x: path[0][0], y: path[0][1] } as any,
        symbol: {
          type: "picture-marker",
          url: "assets/mapIcons/begin.png",
          width: "48px",
          height: "55px",
          yoffset: "20px",
        } as any,
      });
      this.graphicsLayer.add(this.startGraphic);

      // 终点
      this.endGraphic = new this.Graphic({
        geometry: {
          type: "point",
          x: path[path.length - 1][0],
          y: path[path.length - 1][1],
        } as any,
        symbol: {
          type: "picture-marker",
          url: "assets/mapIcons/end.png",
          width: "48px",
          height: "55px",
          yoffset: "20px",
        } as any,
      });
      this.graphicsLayer.add(this.endGraphic);
    }

    const routeGraphic = new this.Graphic({
      geometry: {
        type: "polyline",
        paths: [path],
      } as any,
      attributes: {
        status: "highlight",
      },
    });
    await this.routeLayer.applyEdits({ addFeatures: [routeGraphic] });
    if (this.view.type === "2d") {
      (this.view as __esri.MapView).goTo(routeGraphic);
    } else {
      (this.view as __esri.SceneView).goTo(routeGraphic);
    }
  }

  /** 高亮路径 */
  public async showRoute(params: {
    strategy?: string;
    coordinates?: string;
    signalIds?: string[];
    showPoint?: boolean;
  }): Promise<IResult> {
    const strategy = params.strategy;
    const coordinates = params.coordinates;
    const signalIds = params.signalIds;
    if (strategy) {
      const routeResult = this.routeResults.get(strategy);
      if (!routeResult) {
        return { status: -1, message: "无此策略" };
      } else {
        this.graphicsLayer.removeMany(this.routeCrossGraphics);

        // 将此route对应的graphic置到最上层
        const query = this.routeLayer.createQuery();
        const graphics = (await this.routeLayer.queryFeatures(query)).features;
        let deleteGraphic: __esri.Graphic | undefined;
        graphics.forEach((graphic) => {
          graphic.attributes["status"] = "normal";
          if (graphic.attributes["strategy"] === strategy) {
            deleteGraphic = graphic;
          }
        });
        await this.routeLayer.applyEdits({
          updateFeatures: graphics,
          deleteFeatures: [deleteGraphic],
        });
        if (deleteGraphic) {
          const highlightGraphic = deleteGraphic.clone();
          highlightGraphic.attributes["status"] = "highlight";
          await this.routeLayer.applyEdits({ addFeatures: [highlightGraphic] });
          if (this.view.type === "2d") {
            (this.view as __esri.MapView).goTo(highlightGraphic);
          } else {
            (this.view as __esri.SceneView).goTo(highlightGraphic);
          }
        }

        const crossGraphics = this.strategyGraphics.get(strategy);
        if (crossGraphics) {
          this.routeCrossGraphics = crossGraphics;
          this.graphicsLayer.addMany(crossGraphics);
        }
      }
    } else if (coordinates) {
      this.showRouteInCoordinates(coordinates, params.showPoint);
    }
    if (signalIds) {
      // where
      let whereClause = "";
      signalIds.forEach((signalId) => {
        whereClause += `'${signalId}',`;
      });
      // 去掉最后的逗号
      whereClause = whereClause.substr(0, whereClause.length - 1);
      whereClause = "FSTR_SCATS in (" + whereClause + ")";

      const query = this.signalLayer.createQuery();
      query.where = whereClause;
      const features = (await this.signalLayer.queryFeatures(query)).features;
      this.signalGraphics = features;
    }
    return { status: 0, message: "" };
  }

  /** 清除所有路径和用户输入 */
  public async clearRoutes() {
    // 清除现有路径
    const currentGraphics = (
      await this.routeLayer.queryFeatures(this.routeLayer.createQuery())
    ).features;
    await this.routeLayer.applyEdits({ deleteFeatures: currentGraphics });

    this.graphicsLayer.removeAll();

    // 清除路口点
    this.routeCrossGraphics = [];
    // 清除结果记录
    this.routeResults.clear();
    this.strategyGraphics.clear();

    this.areaGraphics = [];
    this.areaMap.clear();
    this.routeMap.clear();

    this.startPoint = undefined;
    this.endPoint = undefined;
    this.wayPoints = [];
  }

  /** 定位路口 */
  public async focusCross(crossId: string): Promise<IResult> {
    if (this.routeCrossGraphics.length > 0) {
      for (let graphic of this.routeCrossGraphics) {
        if (graphic.attributes["ID"] === crossId) {
          if (this.view.type === "2d") {
            (this.view as __esri.MapView).goTo(graphic);
          } else {
            (this.view as __esri.SceneView).goTo(graphic);
          }
          return { status: 0, message: "" };
        }
      }

      return { status: -1, message: "无此路口" };
    } else {
      const query = this.crossLayer.createQuery();
      query.where = `ID='${crossId}'`;
      const results = (await this.crossLayer.queryFeatures(query)).features;
      if (results.length > 0) {
        const crossGraphic = results[0];
        crossGraphic.symbol = {
          type: "picture-marker",
          url: "assets/mapIcons/road_key.png",
          width: "32px",
          height: "45px",
          yoffset: "15px",
        } as any;
        this.graphicsLayer.add(crossGraphic);
        setTimeout(() => {
          this.graphicsLayer.remove(crossGraphic);
        }, 3000);
        if (this.view.type === "2d") {
          (this.view as __esri.MapView).goTo(crossGraphic);
        } else {
          (this.view as __esri.SceneView).goTo(crossGraphic);
        }
        return { status: 0, message: "" };
      } else {
        return { status: -1, message: "无此路口" };
      }
    }
  }

  /** 轨迹回放时，车辆进入信号机范围回调事件 */
  public intoSignal!: (signalId: string) => void;
  /** 轨迹回放时，车辆离开信号机范围回调事件 */
  public outofSignal!: (signalId: string) => void;

  private playInterval: any;
  private isPaused = false;

  /** 开始播放轨迹 */
  public async play(speed: number): Promise<IResult> {
    // 获取当前正在高亮的路径
    const query = this.routeLayer.createQuery();
    query.where = "status='highlight'";
    const queryResult = (await this.routeLayer.queryFeatures(query)).features;
    if (queryResult.length > 0) {
      const path = (queryResult[0].geometry as __esri.Polyline).paths[0];
      this.isPaused = false;
      this.movePointInPath(speed, path);

      return { status: 0, message: "" };
    }

    return { status: -1, message: "无当前显示路径" };
  }

  /** graphic在polyline上移动 */
  private movePointInPath(speed: number, path: number[][]) {
    // 每秒帧数
    const framePerSecond = 10;
    // 公里/小时 -> 米/秒
    const speedInSeconds = (speed * 1000) / 3600;
    // 每帧前进距离
    const lengthPerFrame = speedInSeconds / framePerSecond;
    const line = turf.lineString(path);
    let currentLength = lengthPerFrame;
    const totalLength = length(line, { units: "meters" });
    const pointArray: Array<Array<number>> = [path[0]];
    // 使用turf.along，将path按照每帧前进距离分割
    while (currentLength < totalLength) {
      const point = along(line, currentLength, {
        units: "meters",
      });
      pointArray.push(point.geometry.coordinates);
      currentLength += lengthPerFrame;
    }

    let currentIndex = 0;
    const carGraphic = new this.Graphic({
      geometry: {
        type: "point",
        x: pointArray[currentIndex][0],
        y: pointArray[currentIndex][1],
      } as any,
      symbol: {
        type: "picture-marker",
        url: "assets/mapIcons/car-top-blue.png",
        width: "50px",
        height: "50px",
      } as any,
    });
    this.graphicsLayer.add(carGraphic);

    // let nearSignalPoint: __esri.Point | null;
    // let nearSignalId: string;

    // 开始移动点位
    console.time("PlayRoute");
    console.log(
      `长度${totalLength.toFixed(2)}米, 速度${speedInSeconds.toFixed(
        2
      )}m/s, 每秒${framePerSecond}帧, 每帧${lengthPerFrame.toFixed(2)}米, 预计${
        pointArray.length
      }帧`
    );

    this.playInterval = setInterval(() => {
      if (this.isPaused) {
        return;
      }

      currentIndex++;
      const point1 = pointArray[currentIndex - 1];
      const point2 = pointArray[currentIndex];
      const angle = this.computeAngle(point1, point2);

      carGraphic.geometry = {
        type: "point",
        x: point2[0],
        y: point2[1],
      } as any;
      (carGraphic.symbol as __esri.PictureMarkerSymbol).angle = angle;

      // 车辆进入和离开信号机周边时发出警报
      // if (!nearSignalPoint) {
      //   // 不在信号机范围内时检测和所有信号机的距离
      //   this.signalGraphics.forEach((graphic) => {
      //     const signalPoint = graphic.geometry as __esri.Point;
      //     const distance = length(
      //       turf.lineString([point2, [signalPoint.x, signalPoint.y]]),
      //       { units: "meters" }
      //     );
      //     // 触发进入报警
      //     if (distance < 30) {
      //       nearSignalPoint = signalPoint;
      //       nearSignalId = graphic.attributes["FSTR_SCATS"];
      //       this.intoSignal(nearSignalId);
      //     }
      //   });
      // } else {
      //   // 在信号机范围内时只检测和当前信号机的距离
      //   const distance = length(
      //     turf.lineString([point2, [nearSignalPoint.x, nearSignalPoint.y]]),
      //     { units: "meters" }
      //   );
      //   // 触发离开报警
      //   if (distance > 30) {
      //     nearSignalPoint = null;
      //     this.outofSignal(nearSignalId);
      //   }
      // }

      // 到达最后一个点位，停止计时
      if (currentIndex === pointArray.length - 1) {
        console.timeEnd("PlayRoute");
        clearInterval(this.playInterval);
      }
    }, 1000 / framePerSecond);
  }

  private computeAngle(pos1: Array<number>, pos2: Array<number>): number {
    const x1 = pos1[0];
    const y1 = pos1[1];
    const x2 = pos2[0];
    const y2 = pos2[1];

    if (x1 === x2) {
      return y2 > y1 ? 0 : 180;
    }

    if (y1 === y2) {
      return x2 > x1 ? 90 : 270;
    }

    const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);
    let px = 1,
      py = 1;
    if (x2 > x1) {
      px = -1;
    }
    if (y2 > y1) {
      py = -1;
    }
    return ((360 - angle + 90) % 360) - px * py * 5 - 180;
  }

  /** 停止轨迹播放 */
  public stop() {
    if (this.playInterval) {
      clearInterval(this.playInterval);
    }
    this.graphicsLayer.removeAll();
  }

  /** 暂停轨迹播放 */
  public pause() {
    if (this.playInterval) {
      this.isPaused = true;
    }
  }

  /** 恢复轨迹播放 */
  public resume() {
    if (this.playInterval) {
      this.isPaused = false;
    }
  }

  private areaMap: Map<
    string,
    { name: string; polygon: __esri.Polygon }
  > = new Map();
  private routeMap: Map<string, string> = new Map();

  /** 路径和哪些区域相交 */
  public async routeHitArea(params: IRouteHitTestParam): Promise<IResult> {
    const route = params.routes[0];
    const areas = params.areas;

    const path = this.pathString2PathArray(route.points);
    const routeLine = new this.Polyline({ paths: [path] });
    this.routeMap.set(route.id, route.points);

    const hittedAreaIdArray: Array<string> = [];
    for (let area of areas) {
      const ring = area.points;
      if (
        ring[0][0] !== ring[ring.length - 1][0] ||
        ring[0][1] !== ring[ring.length - 1][1]
      ) {
        ring.push(ring[0]);
      }
      const areaPolygon = new this.Polygon({ rings: [ring] });
      this.areaMap.set(area.id, { name: area.name, polygon: areaPolygon });

      const isIntersects = await this.geometryEngineAsync.intersects(
        routeLine,
        areaPolygon
      );
      if (isIntersects) {
        hittedAreaIdArray.push(area.id);
      }
    }

    return { status: 0, message: "", result: hittedAreaIdArray };
  }

  /** 区域和哪些路径相交 */
  public async areaHitRoute(params: IRouteHitTestParam): Promise<IResult> {
    const area = params.areas[0];
    const routes = params.routes;

    const ring = area.points;
    if (
      ring[0][0] !== ring[ring.length - 1][0] ||
      ring[0][1] !== ring[ring.length - 1][1]
    ) {
      ring.push(ring[0]);
    }
    const areaPolygon = new this.Polygon({ rings: [ring] });
    this.areaMap.set(area.id, { name: area.name, polygon: areaPolygon });

    const hittedRouteIdArray: Array<string> = [];
    for (let route of routes) {
      const path = this.pathString2PathArray(route.points);
      const routeLine = new this.Polyline({ paths: [path] });
      this.routeMap.set(route.id, route.points);

      const isIntersects = await this.geometryEngineAsync.intersects(
        areaPolygon,
        routeLine
      );
      if (isIntersects) {
        hittedRouteIdArray.push(route.id);
      }
    }
    return { status: 0, message: "", result: hittedRouteIdArray };
  }

  /** 显示相交测试结果 */
  private areaGraphics: Array<__esri.Graphic> = [];
  public async showHitResult(params: IRouteHitTestResultParam) {
    const routeIds = params.routeIds;
    const areaIds = params.areaIds;

    if (routeIds) {
      for (let routeId of routeIds) {
        const coordinates = this.routeMap.get(routeId);
        if (coordinates) {
          await this.showRouteInCoordinates(coordinates);
        }
      }
    }

    if (areaIds) {
      this.areaGraphics = [];
      for (let areaId of areaIds) {
        const area = this.areaMap.get(areaId);
        if (area) {
          const graphic = new this.Graphic({
            geometry: area.polygon,
            symbol: {
              type: "simple-fill",
              color: [3, 169, 244, 0.5],
              style: "solid",
              outline: {
                style: "dash",
                color: [3, 169, 244],
                width: 2,
              },
            } as any,
          });
          this.graphicsLayer.add(graphic);
          this.areaGraphics.push(graphic);

          const areaLabel = new this.Graphic({
            geometry: area.polygon.centroid,
            symbol: {
              type: "text",
              text: area.name,
              color: "white",
              font: {
                // family: "微软雅黑",
                size: 12,
              },
            } as any,
          });
          this.graphicsLayer.add(areaLabel);
          this.areaGraphics.push(areaLabel);
        }
      }
    }
  }

  public async getMultiRoutes(params: IMultiStartRoute): Promise<IResult> {
    const pointGraphics: Array<__esri.Graphic> = [];

    const endPointValue = params.endPoint[0] + "," + params.endPoint[1];
    const endGraphic = new this.Graphic({
      geometry: {
        type: "point",
        x: params.endPoint[0],
        y: params.endPoint[1],
      } as any,
      symbol: {
        type: "picture-marker",
        url: "assets/mapIcons/end.png",
        width: "48px",
        height: "55px",
        yoffset: "20px",
      } as any,
    });
    pointGraphics.push(endGraphic);

    let wayPointsValue = "";
    if (params.wayPoints) {
      params.wayPoints.forEach((wayPoint) => {
        wayPointsValue += wayPoint[0] + "," + wayPoint[1] + ";";
        const wayGraphic = new this.Graphic({
          geometry: {
            type: "point",
            x: wayPoint[0],
            y: wayPoint[1],
          } as any,
          symbol: {
            type: "picture-marker",
            url: "assets/mapIcons/icon_way.png",
            width: "19px",
            height: "31px",
            yoffset: "15px",
          } as any,
        });
        pointGraphics.push(wayGraphic);
      });
      wayPointsValue = wayPointsValue.substr(0, wayPointsValue.length - 1);
    }

    const results: IJBRouteResult[] = [];

    const lineGraphics: Array<__esri.Graphic> = [];
    for (const startPoint of params.startPoints) {
      const startPointValue = startPoint[0] + "," + startPoint[1];
      const startGraphic = new this.Graphic({
        geometry: {
          type: "point",
          x: startPoint[0],
          y: startPoint[1],
        } as any,
        symbol: {
          type: "picture-marker",
          url: "assets/mapIcons/begin.png",
          width: "48px",
          height: "55px",
          yoffset: "20px",
        } as any,
      });
      pointGraphics.push(startGraphic);

      const result = await this.makeRouteByStrategy(
        startPointValue,
        endPointValue,
        wayPointsValue,
        "1",
        "highlight"
      );
      if (result) {
        results.push(result);

        const pathString = result.coordinates;
        const path: Array<Array<number>> = [];
        const pointStringArray = pathString.split(";");
        pointStringArray.forEach((pointString) => {
          const point = pointString.split(",");
          path.push([Number(point[0]), Number(point[1])]);
        });
        const lineGraphic = new this.Graphic({
          geometry: { type: "polyline", paths: [path] } as any,
          symbol: {
            type: "simple-line",
            color: "#007E33",
            width: 4,
          } as any,
        });
        lineGraphics.push(lineGraphic);
      }
    }

    // this.graphicsLayer.addMany(lineGraphics);
    this.graphicsLayer.addMany(pointGraphics);
    if (this.view.type === "2d") {
      (this.view as __esri.MapView).goTo(lineGraphics);
    } else {
      (this.view as __esri.SceneView).goTo(lineGraphics);
    }

    return { status: 0, message: "", result: results };
  }
}
